"use client"

import type { ReactNode } from 'react'
import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import {
  EvaluationAnalysisDetail,
  type EvaluationAnalysisCopy,
} from '@/components/evaluation/evaluation-analysis-detail'
import {
  useEvaluationAnalysis,
  type EvaluationAnalysisPayload,
} from '@/hooks/use-evaluation-analysis'

export type { EvaluationAnalysisPayload }

export type EvaluationAnalysisViewProps = {
  evaluationId?: string
  /** Already-loaded write-up. Skips the signed-in fetch and renders the full analysis. */
  frozenPayload?: EvaluationAnalysisPayload
  subjectLabel?: string
  copy?: EvaluationAnalysisCopy
  actions?: ReactNode
  retryAction?: ReactNode
  depth?: 'full' | 'compact'
}

export function EvaluationAnalysisView({
  evaluationId,
  frozenPayload,
  subjectLabel,
  copy,
  actions,
  retryAction,
  depth = 'full',
}: EvaluationAnalysisViewProps) {
  const frozen = frozenPayload != null
  const { payload, isLoading, isMissing, error } = useEvaluationAnalysis(evaluationId, {
    enabled: !frozen,
  })

  if (frozen) {
    return (
      <EvaluationAnalysisDetail
        payload={frozenPayload}
        subjectLabel={subjectLabel}
        copy={copy}
        depth="full"
      />
    )
  }

  // Show skeleton only until the first payload exists. Do not surface enrichment
  // status copy ("Research in progress…") — analysis should read as ready once scored.
  if (isLoading && !payload) {
    return (
      <EntityDetailSkeleton variant="compact" withPageContainer={false} ariaLabel="Loading analysis" />
    )
  }

  if (error || isMissing || !payload) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {error instanceof Error ? error.message : 'Analysis is not available yet.'}
        </p>
        {retryAction}
      </div>
    )
  }

  return (
    <EvaluationAnalysisDetail
      payload={payload}
      subjectLabel={subjectLabel}
      copy={copy}
      actions={
        retryAction || actions ? (
          <>
            {retryAction}
            {actions}
          </>
        ) : undefined
      }
      depth={depth}
    />
  )
}
